import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { ConvexProvider, ConvexReactClient } from "convex/react";
import "katex/dist/katex.min.css";
// Шрифты teen-english, свои файлы вместо Google Fonts. Кириллица и латиница
// входят в каждый пакет; веса — ровно те, что стоят в токенах appearance.css.
import "@fontsource/onest/400.css";
import "@fontsource/onest/500.css";
import "@fontsource/onest/600.css";
import "@fontsource/onest/700.css";
import "@fontsource/oswald/500.css";
import "@fontsource/oswald/600.css";
import "@fontsource/nunito/700.css";
import "@fontsource/nunito/800.css";
import "@fontsource/nunito/900.css";
import "@fontsource/jetbrains-mono/500.css";
import "@fontsource/jetbrains-mono/700.css";
import "@fontsource/caveat/600.css";
import "@fontsource/caveat/700.css";
import "./styles.css";
// После styles.css: токены оформления переопределяют его прежние значения.
import "./appearance.css";
import "./shell.css";
import "./today.css";
import "./plan.css";
import { App } from "./App";
import { Boundary } from "./ui/Boundary";
import { initTheme } from "./lib/theme";
import { readTutorKey } from "./lib/tutor";
import { readAdminKey } from "./lib/admin";

// Адрес деплоя приходит сборкой, а не жёстко зашит: dev и прод — разные базы,
// и перепутать их значит потерять журнал попыток живого ученика.
const url = import.meta.env.VITE_CONVEX_URL as string | undefined;
if (!url) {
  throw new Error(
    "нет VITE_CONVEX_URL — положите адрес деплоя в .env.local (см. README)",
  );
}

// Кто открыл — решает ключ, а не адрес: роутера нет, и `?k=t-…` открывает
// страницу взрослого, `?k=…` — приложение ученика (docs/14-tutor.md).
//
// Развилка стоит ЗДЕСЬ, а не внутри App: там она была бы ранним возвратом
// после двух десятков хуков, а хук, оказавшийся ниже возврата, роняет React
// на «Rendered more hooks than during the previous render» — в App.tsx это
// уже случалось с порцией задач по теме.
//
// Через lazy(): экран взрослого с редактором не нужен ученику ни разу, и в
// его начальной загрузке ему делать нечего.
const Tutor = lazy(() =>
  import("./screens/Tutor").then((m) => ({ default: m.Tutor })),
);
const tutorKey = readTutorKey();
// `?k=a-…` — админ-страница обратной связи (src/screens/Admin.tsx). Тоже
// через lazy(): ни ученику, ни взрослому она не нужна.
const Admin = lazy(() =>
  import("./screens/Admin").then((m) => ({ default: m.Admin })),
);
const adminKey = readAdminKey();

// Атрибуты оформления уже стоят — их поставил скрипт в index.html. Здесь
// подхватывается `?theme=`.
initTheme();

const root = document.getElementById("root");
if (!root) throw new Error("нет #root");

createRoot(root).render(
  <StrictMode>
    <ConvexProvider client={new ConvexReactClient(url)}>
      {/* Граница ВНУТРИ провайдера: снаружи она поймала бы то же самое, но
          экран отказа остался бы без клиента Convex. */}
      <Boundary>
        {adminKey !== "" ? (
          <Suspense fallback={<div className="page" />}>
            <Admin adminKey={adminKey} />
          </Suspense>
        ) : tutorKey === "" ? (
          <App />
        ) : (
          <Suspense fallback={<div className="page" />}>
            <Tutor tutorKey={tutorKey} />
          </Suspense>
        )}
      </Boundary>
    </ConvexProvider>
  </StrictMode>,
);
